// Duplication Rule API service for Multi-Territory
// Uses dedicated duplication-rules endpoints (NOT generic MT_DATA_MASTER_*_API)

import {
  MT_DUPLICATE_RULE_DELETE_API,
  MT_DUPLICATE_RULE_DOWNLOAD_API,
  MT_DUPLICATE_RULE_LIST_API,
  MT_DUPLICATE_RULE_SAVE_API,
  MT_DUPLICATE_RULE_UPDATE_API,
  MT_TRANSACTION_DATA_META_DATA,
} from '@/multi-territory/mt-services/mt-api-endpoints'
import {
  mtBuildRequestHeader,
  mtHandleErrorResponse,
  mtHandleRequestFailure,
  mtParseJsonResponse,
  mtParseMessageResponse,
  mtUrlBuilder,
  type MTApiResponse,
} from '@/multi-territory/mt-services/mt-api-utils'

// --- Request/Response Types ---

export interface DuplicationRuleSearchFilter {
  fieldName: string
  operator: string
  value: string
}

export interface DuplicationRuleListRequest {
  pageFilter: {
    currentPage: number
    pageSize: number
    includeCount?: boolean
    sortField: string
    sortOrder: 'ASC' | 'DESC'
  }
  searchCOList?: DuplicationRuleSearchFilter[]
  countryCode: string
}

export interface DuplicationRuleListResponse {
  content: DuplicationRuleRow[]
  totalElements: number
}

export interface DuplicationRuleRow {
  uuid: string
  ruleName: string
  ruleStatus: string
  active?: boolean
  statusMessage?: string
  parameter_1?: string
  parameter_2?: string
  parameter_3?: string
  parameter_4?: string
  parameter_5?: string
  parameter_6?: string
  parameter_7?: string
  parameter_8?: string
  createdBy?: string
  createdDate?: string
  lastModifiedBy?: string
  lastModifiedDate?: string
}

export interface DuplicationRuleCreateRequest {
  ruleName: string
  tanMasterUuid: string | null
  entityMasterUuid?: string | null
  countryCode: string
  parameters: string[]
}

export interface DuplicationRuleUpdateRequest {
  ruleName: string
  tanMasterUuid: string | null
  entityMasterUuid?: string | null
  countryCode: string
  parameters: string[]
}

// --- API Functions ---

/**
 * Fetches duplication rules list.
 * POST /wht-mt-admin/api/v1/duplication-rules/tanUuid/{identifierUuid}?countryCode={countryCode}
 *
 * @param identifierUuid - tanMasterUuid (when tanApplicable=true) or entityMasterUuid (when false)
 * @param options.tanApplicable - determines payload fields (default: true)
 */
export async function fetchDuplicationRules(
  identifierUuid: string,
  payload: DuplicationRuleListRequest,
  options?: { tanApplicable?: boolean }
): Promise<MTApiResponse<DuplicationRuleListResponse>> {
  try {
    const tanApplicable = options?.tanApplicable ?? true
    const identifierFields = tanApplicable
      ? { tanMasterUuid: identifierUuid, entityMasterUuid: null }
      : { entityMasterUuid: identifierUuid, tanMasterUuid: null }
    const url = mtUrlBuilder(MT_DUPLICATE_RULE_LIST_API)
    const { pageFilter, searchCOList, countryCode } = payload
    // Backend expects pageFilter + searchCOList nested under
    // commonFetchDataRequestCO, with countryCode and the tan/entity
    // identifier at the top level.
    const enrichedPayload = {
      countryCode,
      ...identifierFields,
      commonFetchDataRequestCO: {
        pageFilter,
        searchCOList: searchCOList ?? [],
      },
    }
    const requestInit = mtBuildRequestHeader('POST', enrichedPayload)
    const response = await fetch(url, requestInit)

    if (!response.ok) {
      return await mtHandleRequestFailure(response)
    }

    return await mtParseJsonResponse<DuplicationRuleListResponse>(response)
  } catch (error) {
    return mtHandleErrorResponse(error)
  }
}

/**
 * Creates a new duplication rule.
 * POST /wht-mt-admin/api/v1/duplication-rules/addrule
 *
 * Body: { ruleName, taxMasterUuid, countryCode, parameters }
 */
export async function createDuplicationRule(
  record: DuplicationRuleCreateRequest
): Promise<MTApiResponse<string>> {
  try {
    const url = mtUrlBuilder(MT_DUPLICATE_RULE_SAVE_API)
    const requestInit = mtBuildRequestHeader('POST', record)
    const response = await fetch(url, requestInit)

    if (!response.ok) {
      return await mtHandleRequestFailure(response)
    }

    return await mtParseMessageResponse(response)
  } catch (error) {
    return mtHandleErrorResponse(error)
  }
}

/**
 * Updates an existing duplication rule.
 * PUT /wht-mt-admin/api/v1/duplication-rules/{uuid}
 *
 * Body: { ruleName, taxMasterUuid, countryCode, parameters }
 */
export async function updateDuplicationRule(
  record: DuplicationRuleUpdateRequest
): Promise<MTApiResponse<string>> {
  try {
    const url = mtUrlBuilder(MT_DUPLICATE_RULE_UPDATE_API)
    const requestInit = mtBuildRequestHeader('POST', record)
    const response = await fetch(url, requestInit)

    if (!response.ok) {
      return await mtHandleRequestFailure(response)
    }

    return await mtParseMessageResponse(response)
  } catch (error) {
    return mtHandleErrorResponse(error)
  }
}

/**
 * Deletes an existing duplication rule.
 * DELETE /wht-mt-admin/api/v1/duplication-rules/{uuid}?countryCode={countryCode}&entityMasterUuid={entityUuid}
 */
export async function deleteDuplicationRule(
  countryCode: string,
  uuid: string
): Promise<MTApiResponse<string>> {
  try {
    const url = mtUrlBuilder(MT_DUPLICATE_RULE_DELETE_API)
    const payload = {
      countryCode,
      uuid,
    }
    const requestInit = mtBuildRequestHeader('POST', payload)
    const response = await fetch(url, requestInit)

    if (!response.ok) {
      return await mtHandleRequestFailure(response)
    }

    return await mtParseMessageResponse(response)
  } catch (error) {
    return mtHandleErrorResponse(error)
  }
}

/**
 * Downloads duplication rules as Excel file.
 * POST /wht-mt-admin/api/v1/duplication-rules/download/tanUuid/{identifierUuid}?countryCode={countryCode}
 *
 * @param identifierUuid - tanMasterUuid (when tanApplicable=true) or entityMasterUuid (when false)
 * @param options.tanApplicable - determines payload fields (default: true)
 */
export async function downloadDuplicationRules(
  countryCode: string,
  identifierUuid: string,
  payload?: DuplicationRuleListRequest,
  options?: { tanApplicable?: boolean }
): Promise<MTApiResponse<Blob>> {
  try {
    const tanApplicable = options?.tanApplicable ?? true
    const identifierFields = tanApplicable
      ? { tanMasterUuid: identifierUuid, entityMasterUuid: null }
      : { entityMasterUuid: identifierUuid, tanMasterUuid: null }
    const url = mtUrlBuilder(MT_DUPLICATE_RULE_DOWNLOAD_API)
    const { pageFilter, searchCOList } = payload ?? ({} as DuplicationRuleListRequest)
    const enrichedPayload = {
      countryCode,
      ...identifierFields,
      commonFetchDataRequestCO: {
        ...(pageFilter ? { pageFilter } : {}),
        searchCOList: searchCOList ?? [],
      },
    }
    const requestInit = mtBuildRequestHeader('POST', enrichedPayload)
    const response = await fetch(url, requestInit)

    if (!response.ok) {
      return await mtHandleRequestFailure(response)
    }

    const blob = await response.blob()
    return { success: true, data: blob }
  } catch (error) {
    return mtHandleErrorResponse(error)
  }
}

/**
 * Fetches transactional data metadata (parameter options for duplication rules).
 * GET /wht-mt-admin/api/v1/data/{countryCode}/TRANSACTIONAL_DATA/metadata
 */
export async function fetchDuplicationRuleParameterOptions(
  countryCode: string
): Promise<MTApiResponse<any[]>> {
  try {
    const url = mtUrlBuilder(MT_TRANSACTION_DATA_META_DATA(countryCode))
    const requestInit = mtBuildRequestHeader('GET')
    const response = await fetch(url, requestInit)

    if (!response.ok) {
      return await mtHandleRequestFailure(response)
    }

    return await mtParseJsonResponse<any[]>(response)
  } catch (error) {
    return mtHandleErrorResponse(error)
  }
}
/**
 * Triggers a file download from a Blob response.
 */
export function triggerDuplicationRuleDownload(
  blob: Blob,
  filename: string
): void {
  const url = window.URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  document.body.appendChild(anchor)
  anchor.click()
  document.body.removeChild(anchor)
  window.URL.revokeObjectURL(url)
}
